/**
 * Kiki — the scorecard drafts Cora screens against. For every open role with a kick-off sheet and
 * no scorecard yet, Kiki drafts one (Google Doc in the Kiki folder, linked from the hiring
 * database) and DMs the recruiter to review it before ticking "Screened by Cora".
 *
 * Shows the roles waiting for a draft (with "Generate scorecard"), every draft Kiki wrote, and why
 * a role could not be drafted.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { CoraApiError, fetchScorecards, generateScorecardNow, type CoraMode, type KikiStatus } from '../../services/coraApi';
import { fmtWhen } from './coraFormat';
import { useCoraWrite } from './useCoraWrite';

const STATUS: Record<KikiStatus, { label: string; className: string }> = {
  running: { label: 'Drafting…', className: 'text-zinc-600' },
  drafted: { label: 'Drafted (preview)', className: 'text-orange' },
  published: { label: 'Published', className: 'text-green' },
  failed: { label: 'Failed', className: 'text-red' },
};

const DM_LABEL: Record<string, string> = {
  sent: 'Recruiter notified',
  'no-recruiter': 'No recruiter on the role',
  'not-on-slack': 'Recruiter not found on Slack',
};

const ISSUE_LABEL: Record<string, string> = {
  'invalid-posting-id': 'Lever posting ID is malformed',
  'invalid-kickoff-sheet': 'Kick-off sheet link is not a Google Doc',
};

export default function ScorecardsTab({ mode }: { mode: CoraMode | null }) {
  const queryClient = useQueryClient();
  const canWrite = useCoraWrite();
  const [busy, setBusy] = useState<string | null>(null);
  const data = useQuery({ queryKey: ['cora', 'scorecards'], queryFn: fetchScorecards, staleTime: 60_000 });
  const live = mode === 'live';
  const canGenerate = Boolean(mode) && mode !== 'off' && (!live || canWrite);

  const generate = async (positionId: string) => {
    if (!mode) return;
    setBusy(positionId);
    void logAnalyticsEvent('kiki_generate_clicked', { live });
    try {
      const result = await generateScorecardNow(positionId, mode);
      void logAnalyticsEvent('kiki_generate_finished', { status: result.status });
      if (result.status === 'already-handled') {
        showToast({ status: 'info', description: 'Kiki is already drafting this scorecard — it will appear in the list shortly.' });
      } else if (result.status === 'failed') {
        showToast({ status: 'error', description: 'Kiki could not draft this scorecard — see the reason in the list.' });
      } else if (result.status === 'published') {
        showToast({
          status: 'success',
          description: `Scorecard created and linked in the hiring database. ${
            result.dm === 'sent' ? 'The recruiter was messaged.' : `The recruiter was NOT messaged: ${DM_LABEL[result.dm ?? ''] ?? result.dm ?? 'unknown reason'}.`
          }`,
        });
      } else {
        showToast({ status: 'success', description: 'Scorecard drafted (preview — nothing was created or sent).' });
      }
    } catch (err) {
      if (err instanceof CoraApiError && err.code === 'mode-changed') void queryClient.invalidateQueries({ queryKey: ['cora', 'status'] });
      showToast({ status: 'error', description: err instanceof Error ? err.message : 'Kiki could not draft this scorecard.' });
    } finally {
      setBusy(null);
      void queryClient.invalidateQueries({ queryKey: ['cora', 'scorecards'] });
    }
  };

  if (data.isLoading) return <p className="text-sm text-zinc-600">Loading scorecards…</p>;
  if (data.isError) return <p role="alert" className="text-sm text-red">The scorecards could not be loaded.</p>;
  const { runs, waiting, issues } = data.data!;

  return (
    <div className="flex flex-col gap-6">
      <p className="max-w-3xl text-sm text-zinc-600">
        Kiki drafts the scorecard for every open role with a kick-off sheet and no scorecard yet — from the kick-off sheet and the
        Lever posting — and messages the recruiter to review it. Cora screens against it once &quot;Screened by Cora&quot; is ticked.
      </p>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-zinc-900">Waiting for a scorecard ({waiting?.length ?? '—'})</h2>
        {waiting === null && <p className="text-sm text-orange">The hiring database could not be read just now.</p>}
        {waiting && waiting.length === 0 && <p className="text-sm text-zinc-600">Every open role with a kick-off sheet has a scorecard.</p>}
        <ul className="flex flex-col gap-1">
          {(waiting ?? []).map((p) => (
            <li key={p.positionId} className="flex flex-wrap items-center gap-3 rounded-sm border border-solid border-pearl p-2 text-sm">
              <span className="font-medium text-zinc-900">{p.title}</span>
              <span className="text-zinc-500">#{p.positionId}{p.recruiterName ? ` · ${p.recruiterName}` : ''}</span>
              {canGenerate && (
                <Button variant="outline" className="ml-auto" onClick={() => generate(p.positionId)} disabled={busy !== null}>
                  {busy === p.positionId ? 'Kiki is drafting…' : 'Generate scorecard'}
                </Button>
              )}
            </li>
          ))}
        </ul>
        {live && !canWrite && <p className="mt-1 text-xs text-zinc-500">Generating in live mode needs Cora Operator access.</p>}
        {issues.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1 text-sm text-zinc-700">
            {issues.map((i) => (
              <li key={`${i.positionId}-${i.issue}`}>
                <strong>{i.title} (#{i.positionId})</strong>: {ISSUE_LABEL[i.issue] ?? i.issue}
                {i.detail ? ` — ${i.detail}` : ''}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-zinc-900">Scorecards Kiki drafted</h2>
        {runs.length === 0 ? (
          <p className="text-sm text-zinc-600">None yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-sm border border-solid border-pearl">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-solid border-pearl text-xs uppercase text-zinc-500">
                <tr>
                  {['Role', 'Status', 'Scorecard', 'Recruiter', 'When'].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-b border-solid border-pearl last:border-0 text-zinc-700">
                    <td className="px-3 py-2 font-medium text-zinc-900">{r.title}<span className="block text-xs text-zinc-500">#{r.positionId}</span></td>
                    <td className={`px-3 py-2 ${STATUS[r.status].className}`}>{STATUS[r.status].label}{r.error ? <span className="block text-xs">{r.error}</span> : null}</td>
                    <td className="px-3 py-2">{r.docUrl ? <a className="text-trustedBlue underline" href={r.docUrl} target="_blank" rel="noreferrer">Open</a> : '—'}</td>
                    <td className="px-3 py-2">{r.recruiterName || '—'}<span className="block text-xs text-zinc-500">{r.dm ? (DM_LABEL[r.dm] ?? r.dm) : ''}</span></td>
                    <td className="whitespace-nowrap px-3 py-2 text-zinc-600">{fmtWhen(r.finishedAt || r.startedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
